import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ModalConfirm from '../../../components/ModalConfirm';
import { OfferAPI } from '../../../api/payments/offer/api';
import { PlansAPI } from '../../../api/payments/plans/api';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { DetailRow } from '../../ClienteDados/components/Financeiro/Financial/components/DetailRow';
import '../../ClienteDados/components/Financeiro/Financial/components/FinancialActionsMenu.css';
import { PAYMENT_METHOD_LABELS } from '../../PaymentPlans/utils';
import { DetailListRow } from '../_components/DetailListRow';
import {
  OfferStatusBadge,
  getOfferStatus,
} from '../_components/OfferStatusBadge';
import { OrderSummaryCard } from '../_components/OrderSummaryCard';
import { PlanDetailsPanel } from '../_components/PlanDetailsPanel';
import { JdsButton } from '../../../components/jds/JdsButton';
import '../../../components/jds/jds.css';
import { buildOfferSummary } from '../create/create-offer.bootstrap.hook';
import { copyOfferLink, formatDateTime, formatExpiresAt } from '../utils';

async function loadPlan(offer) {
  if (!offer?.planName) return null;
  const findPlan = async (active) => {
    const { data } = await PlansAPI.list({ name: offer.planName, active });
    return (
      data?.content?.find((plan) => plan.id === offer.planId) ??
      data?.content?.find((plan) => plan.name === offer.planName)
    );
  };
  return (await findPlan(true)) ?? findPlan(false);
}

export default function OfferDetails() {
  const { id } = useParams();
  const history = useHistory();
  const [offer, setOffer] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmExpire, setConfirmExpire] = useState(false);
  const [expiring, setExpiring] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPlan(null);
    OfferAPI.getV2(id)
      .then((response) => {
        if (cancelled) return;
        const data = response.data ?? response;
        setOffer(data);
        // O painel do plano é complementar: se falhar, a tela segue sem ele.
        loadPlan(data)
          .then((loaded) => {
            if (!cancelled) setPlan(loaded);
          })
          .catch((error) => console.error('Erro ao buscar o plano:', error));
      })
      .catch(() => {
        if (!cancelled) setOffer(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading)
    return <div className="jds-scope">Carregando dados da oferta...</div>;
  if (!offer) return <div className="jds-scope">Oferta não encontrada.</div>;

  const addons = (offer.addons ?? []).map((addon) => ({
    ...addon,
    quantity: addon.quantity ?? 1,
  }));
  const { coupon, customerEmail } = offer;
  const paymentMethods = (offer.allowedPaymentMethods ?? [])
    .map((method) => PAYMENT_METHOD_LABELS[method] ?? method)
    .join(', ');

  // Mesmo resumo da criação. Os totais vêm da API: o desconto é a diferença
  // entre plano + add-ons e o preço final da oferta.
  const summaryItems = addons.map((addon, index) => ({
    key: addon.id ?? `${addon.itemId}-${index}`,
    itemId: addon.itemId,
    quantity: addon.quantity,
    item: {
      id: addon.itemId,
      name: addon.itemName,
      value: addon.total / addon.quantity,
    },
  }));
  const subtotal =
    Number(offer.planValue ?? 0) +
    addons.reduce((sum, addon) => sum + Number(addon.total ?? 0), 0);
  const discountAmount =
    offer.discountAmount != null
      ? Number(offer.discountAmount)
      : Math.max(0, subtotal - Number(offer.finalPrice ?? 0));
  const addonQuantities = Object.fromEntries(
    addons.map((addon) => [addon.itemId, addon.quantity]),
  );

  // Estimativa por ciclo (mesma do form) a partir das regras do cupom; os
  // totais do 1º ciclo acima seguem os valores da API.
  const getCycleTotal = (cycle) =>
    buildOfferSummary(
      { id: offer.planId, amount: Number(offer.planValue ?? 0) },
      summaryItems,
      coupon,
      cycle,
    ).finalTotal;

  const status = getOfferStatus(offer);

  const handleExpire = async () => {
    setExpiring(true);
    try {
      await OfferAPI.expireV2(offer.codeHash);
      toast.success('Oferta expirada com sucesso!');
      setOffer({ ...offer, expiredAt: new Date().toISOString() });
      setConfirmExpire(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível expirar a oferta.',
      );
    } finally {
      setExpiring(false);
    }
  };

  // Abre a criação pré-preenchida (plano, produtos, cupom, métodos). Nome,
  // cliente e validade não se copiam: nome repetiria, e-mail é de outro
  // cliente e a data antiga pode já ter vencido.
  const handleDuplicate = () =>
    history.push('/payments/offers/add', {
      offer: {
        ...offer,
        name: offer.name ? `${offer.name}_COPIA` : '',
        customerEmail: '',
        expirationDate: null,
      },
    });

  const goToList = () => history.push('/payments/offers');

  return (
    <div className="jds-scope">
      <div className="mb-5">
        <span
          role="button"
          tabIndex={0}
          onClick={goToList}
          onKeyDown={(event) => event.key === 'Enter' && goToList()}
          className="text-muted"
          style={{ cursor: 'pointer' }}
        >
          &lt; Painel ADMIN
        </span>
        <span className="text-muted"> &bull; Ofertas</span>
      </div>

      <div
        className="d-flex align-items-start"
        style={{ gap: 'var(--space-16)' }}
      >
        <div className="jds-card" style={{ flex: '1 1 600px', minWidth: 0 }}>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ marginBottom: 'var(--space-24)', gap: 'var(--space-16)' }}
          >
            <h1
              className="jds-title-md"
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={offer.name}
            >
              {offer.name ?? 'Detalhes da oferta'}
            </h1>
            <div
              className="d-flex"
              style={{ gap: 'var(--space-8)', flexShrink: 0 }}
            >
              {status === 'active' && (
                <JdsButton
                  variant="outlined"
                  color="error"
                  disabled={!offer.codeHash}
                  onClick={() => setConfirmExpire(true)}
                >
                  Expirar
                </JdsButton>
              )}
              <JdsButton
                variant="outlined"
                disabled={status === 'expired'}
                title={
                  status === 'expired'
                    ? 'Oferta expirada não pode ser editada'
                    : undefined
                }
                onClick={() => history.push(`/payments/offers/${id}/edit`)}
              >
                Editar
              </JdsButton>
              <JdsButton
                variant="outlined"
                color="neutral"
                onClick={handleDuplicate}
              >
                Duplicar
              </JdsButton>
              <JdsButton
                disabled={!offer.codeHash}
                onClick={() => copyOfferLink(offer)}
              >
                Copiar link
              </JdsButton>
            </div>
          </div>

          <div className="mb-6 d-flex flex-column" style={{ gap: 12 }}>
            <DetailRow label="Status">
              <OfferStatusBadge offer={offer} />
            </DetailRow>
            <DetailRow label="Cadastrada em">
              {offer.createdAt ? formatExpiresAt(offer.createdAt) : '-'}
            </DetailRow>
            <DetailRow label="Data de vencimento">
              {formatExpiresAt(offer.expirationDate)}
            </DetailRow>
            {offer.expiredAt && (
              <DetailRow label="Expirada em">
                {formatDateTime(offer.expiredAt)}
              </DetailRow>
            )}
            {offer.paidAt && (
              <DetailRow label="Paga em">
                {formatDateTime(offer.paidAt)}
              </DetailRow>
            )}
            {offer.paidWith && (
              <DetailRow label="Paga com">
                {PAYMENT_METHOD_LABELS[offer.paidWith] ?? offer.paidWith}
              </DetailRow>
            )}
            {offer.createdByEmail && (
              <DetailRow label="Criada por">{offer.createdByEmail}</DetailRow>
            )}
            <DetailRow label="Métodos de pagamento">
              {paymentMethods || '-'}
            </DetailRow>
            <DetailRow label="E-mail do cliente">
              {customerEmail ?? '-'}
            </DetailRow>
            <DetailRow label="Plano base">{offer.planName ?? '-'}</DetailRow>
            <DetailRow label="Valor do plano" boldValue>
              {formatCurrency(offer.planValue)}
            </DetailRow>
          </div>

          <hr className="jds-divider" />

          <div className="mb-6">
            <h3 className="jds-title-xs mb-3">Produtos</h3>
            {addons.length > 0 ? (
              <div className="d-flex flex-column" style={{ gap: 12 }}>
                {addons.map((addon, index) => (
                  <DetailListRow
                    key={addon.id ?? `${addon.name}-${index}`}
                    id={`offer-addon-actions-${addon.id ?? index}`}
                    labelPrimary={addon.itemName}
                    labelSecondary={`${addon.quantity}x ${
                      addon.quantity > 1 ? 'itens' : 'item'
                    }`}
                    value={formatCurrency(addon.total)}
                    actionLabel="Detalhes do Produto"
                    onAction={() =>
                      history.push(`/payments/items/${addon.itemId}`)
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted mb-0">Nenhum produto cadastrado.</p>
            )}
          </div>

          <hr className="jds-divider" />

          <div className="mb-6">
            <h3 className="jds-title-xs mb-3">Cupom</h3>
            {coupon ? (
              <DetailListRow
                id="offer-coupon-actions"
                labelPrimary={coupon.name ?? coupon.code}
                actionLabel="Detalhes do cupom"
                onAction={() =>
                  history.push({
                    pathname: `/payments/coupons/${coupon.id}`,
                    state: { coupon },
                  })
                }
              />
            ) : (
              <p className="text-muted mb-0">Nenhum cupom aplicado.</p>
            )}
          </div>

          <OrderSummaryCard
            planName={offer.planName ?? ''}
            planAmount={Number(offer.planValue ?? 0)}
            items={summaryItems}
            discountAmount={discountAmount}
            couponCode={coupon?.code ?? coupon?.name}
            couponRules={coupon?.rules}
            planId={offer.planId}
            getCycleTotal={getCycleTotal}
            finalTotal={Number(offer.finalPrice ?? 0)}
          />
        </div>

        {plan && (
          <PlanDetailsPanel plan={plan} addonQuantities={addonQuantities} />
        )}
      </div>

      {confirmExpire && (
        <ModalConfirm
          title="Expirar oferta"
          message="Tem certeza que deseja expirar esta oferta? O link deixará de funcionar para o cliente e a oferta não poderá mais ser editada."
          onConfirm={handleExpire}
          onClose={() => setConfirmExpire(false)}
          isLoading={expiring}
        />
      )}
    </div>
  );
}
